// url resolver

import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import qs from 'query-string';

import { useGetLayoutUrlResolver } from 'api';
import { getTranslationByLanguage } from 'api/helpers';
import { isWiseUiEnabled } from 'config/wiseUi';

import { Products, WiseProducts, WiseProduct, Product, NoMatch, CmsPage, Home } from 'pages';

interface IParams {
  category_id?: string;
  product_id?: string;
  mode?: 'PROMOTIONS' | 'NEWS' | 'BESTSELLERS';
}

const UrlResolver = () => {
  const { pathname, search, state } = useLocation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const getPosition = (string: string, subString: string, index: number) => {
    return string.split(subString, index).join(subString).length;
  };

  const urlPrefix = pathname.slice(0, getPosition(pathname, '/', 2));
  const urlKey = pathname.replace(urlPrefix, '').replace('/', '');
  const currentLanguage = i18n.language;

  const {
    data: urlResolverData,
    refetch: getUrlResolver,
    status,
    error
  } = useGetLayoutUrlResolver(
    { urlKey },
    {
      enabled: false,
      keepPreviousData: true,
      onSuccess: (data) => {
        if (data.page_type !== 'CMS_PAGE') {
          const { ...rest } = qs.parse(data?.parameters || '');
          const urlParams = { ...rest, ...qs.parse(search) };
          navigate(pathname + `?${qs.stringify(urlParams)}`, { replace: true, state });
        }
      }
    }
  );

  const seoData = useMemo(() => {
    if (!urlResolverData) return null;

    return {
      title: getTranslationByLanguage(urlResolverData.meta_title, currentLanguage),
      description: getTranslationByLanguage(urlResolverData.meta_description, currentLanguage),
      keywords: getTranslationByLanguage(urlResolverData.meta_keywords, currentLanguage),
      h1: getTranslationByLanguage(urlResolverData.h1, currentLanguage),
      ogTitle: getTranslationByLanguage(urlResolverData.open_graph_title, currentLanguage), // tytuł Open Graph dla aktualnego języka (dla Facebook, LinkedIn)
      ogDescription: getTranslationByLanguage(
        urlResolverData.open_graph_description,
        currentLanguage
      ), // opis Open Graph dla aktualnego języka
      ogImage: urlResolverData.open_graph_image
    };
  }, [urlResolverData, currentLanguage]);

  useEffect(() => {
    getUrlResolver();
  }, [urlKey, currentLanguage]);

  const renderContent = () => {
    const { category_id, product_id, mode } = qs.parse(
      urlResolverData?.parameters || ''
    ) as IParams;

    const searchParams = qs.parse(search);
    const modeFromUrl = searchParams.mode as 'PROMOTIONS' | 'NEWS' | 'BESTSELLERS' | undefined;
    const finalMode = mode || modeFromUrl;

    if (urlResolverData?.page_type === 'PRODUCTS') {
      const ProductsList = isWiseUiEnabled() ? WiseProducts : Products;
      return (
        <ProductsList
          key="products-list"
          categoryId={category_id}
          mode={finalMode}
          h1={seoData?.h1}
          hasSeoTitle={!!seoData?.title}
        />
      );
    }

    if (urlResolverData?.page_type === 'PRODUCT' && product_id) {
      const props = { id: product_id, h1: seoData?.h1, hasSeoTitle: !!seoData?.title };
      return isWiseUiEnabled() ? (
        <WiseProduct {...props} />
      ) : (
        <Product {...props} />
      );
    }

    if (urlResolverData?.page_type === 'CMS_PAGE') {
      return <CmsPage sectionId="SUBPAGE" articleId={urlResolverData.parameters} h1={seoData?.h1} hasSeoTitle={!!seoData?.title}/>;
    }

    if (urlResolverData?.page_type === 'HOME_PAGE') {
      return <Home />;
    }

    if (status === 'error' && error.httpStatus === 404) {
      return <NoMatch />;
    }

    return null;
  };

  return (
    <>
      <Helmet>
        {seoData?.title && <title>{seoData.title}</title>}
        {seoData?.description && <meta name="description" content={seoData.description} />}
        {seoData?.keywords && <meta name="keywords" content={seoData.keywords} />}
        {seoData?.ogTitle && <meta property="og:title" content={seoData.ogTitle} />}
        {seoData?.ogDescription && (
          <meta property="og:description" content={seoData.ogDescription} />
        )}
        {seoData?.ogImage && <meta property="og:image" content={seoData.ogImage} />}
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />

        {urlResolverData?.href_lang?.map((shortcut, i) => (
          <link
            key={i}
            rel="alternate"
            href={`${window.location.origin}/${shortcut.language}/${shortcut.url_shortcut.replace(/^\//, '')}`}
            hrefLang={shortcut.language}
          />
        ))}
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      {renderContent()}
    </>
  );
};

export default UrlResolver;
